import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { omit } from 'lodash';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { t } from 'ttag';

import { list } from '@domains/People/fields-service';

import { ITEM_TYPE, getStaticFields } from './Form/FormTab/constants';
import {
  createEmailTemplate,
  createSection,
  getEmailTemplates,
  getFormItems,
  getPreviewLink,
  getSection,
  NEW_EMAIL_TEMPLATE_ID,
  updateEmailTemplate,
  updateFormItems,
  updateSection,
} from './service';

// Preview link expires after 1 hour, but we refetch it a little
// more often than that to account for any potential latency.
const PREVIEW_LINK_REFETCH_INTERVAL = 57 * 60 * 1000;

export const registrationPreviewURLOptions = (eventId, { embed }) => ({
  queryKey: ['events', eventId, 'registration', 'registrationPreviewURL'],
  queryFn: () => getPreviewLink(eventId),
  select: data => {
    const url = new URL(data.link);
    const queryParam = embed ? 'embed' : 'reg_only';
    url.searchParams.set(queryParam, 'true');

    return url.toString();
  },
  refetchInterval: PREVIEW_LINK_REFETCH_INTERVAL,
});

export const useRegistrationPreviewURL = (options = {}) => {
  const { embed = false } = options;
  const { eventId } = useParams();
  return useQuery(registrationPreviewURLOptions(eventId, { embed }));
};

const transformSection = ({ id: sectionId, formItems, ...rest }) => ({
  ...omit(rest, 'createdAt', 'updatedAt'),
  id: sectionId,
  type: 'section',
  required: t`N/A`,
  children: formItems
    .sort((itemA, itemB) => {
      return itemA.order - itemB.order;
    })
    .map(({ id: itemId, itemType, customField, ...restItems }) => ({
      ...omit(restItems, 'createdAt', 'updatedAt'),
      id: itemId,
      name:
        (itemType === ITEM_TYPE.CustomField && customField.name) ||
        (itemType === ITEM_TYPE.RichText && restItems.name) ||
        getStaticFields().find(({ fieldType }) => fieldType === itemType).name,
      itemType,
      customField,
      type: itemType === ITEM_TYPE.CustomField ? customField.type : 'text',
      parentId: sectionId,
    })),
});

const transformSections = sections => {
  const result = sections.map(transformSection).sort((sectionA, sectionB) => {
    return sectionA.order - sectionB.order;
  });

  return result;
};

export const registrationFormOptions = (eventId, { removeProtectedFields = false } = {}) => ({
  queryKey: ['events', eventId, 'registration', 'form', 'sections'],
  queryFn: () => getFormItems(eventId),
  select: data => {
    if (!removeProtectedFields) {
      return data;
    }

    const sections = data.data.reduce((acc, section) => {
      if (!section.protected) {
        acc.push(section);
        return acc;
      }

      // if there is any unprotected field in a protected section, we keep that section with unprotected fields.
      const unprotectedItems = section.formItems.filter(item => !item.protected);
      if (unprotectedItems.length > 0) {
        acc.push({ ...section, formItems: unprotectedItems });

        return acc;
      }

      return acc;
    }, []);

    return {
      ...data,
      data: sections,
    };
  },
});

export const unlinkedFieldsOptions = eventId => ({
  queryKey: ['events', eventId, 'registration', 'form', 'unlinked-fields'],
  queryFn: () => list(eventId, { fieldsWithoutLinkType: 'registration' }),
});

export const useRegistrationForm = ({ removeProtectedFields, queryOptions } = {}) => {
  const { eventId } = useParams();
  const { data, ...rest } = useQuery({
    ...registrationFormOptions(eventId, { removeProtectedFields }),
    ...queryOptions,
  });

  const memoizedData = useMemo(() => {
    if (!data) {
      return [];
    }

    return transformSections(data.data);
  }, [data]);

  return {
    ...rest,
    data: memoizedData,
  };
};

export const useRegistrationFormItems = (options = {}) => {
  const { eventId } = useParams();

  const { data: formData, isLoading: isFormLoading } = useRegistrationForm(options);
  const { data: unlinkedFieldsData, isLoading: isUnlinkedFieldsLoading } = useQuery(
    unlinkedFieldsOptions(eventId),
  );

  const { sidebarFields, isFieldSearchEnabled } = useMemo(() => {
    const usedFields = new Set(
      formData
        .map(section => section.children)
        .flat()
        .map(item => item.name),
    );

    const unlinkedFields = unlinkedFieldsData ?? [];

    return {
      sidebarFields: getStaticFields()
        .filter(field => !usedFields.has(field.name))
        .filter(field => (options.removeProtectedFields ? !field.protected : true))
        .concat(unlinkedFields),
      isFieldSearchEnabled: unlinkedFields.length > 0,
    };
  }, [formData, options.removeProtectedFields, unlinkedFieldsData]);

  return {
    sections: formData,
    sidebarFields,
    isFieldSearchEnabled,
    isLoading: isFormLoading || isUnlinkedFieldsLoading,
  };
};

export const useRegistrationUpdateFormMutation = () => {
  const queryClient = useQueryClient();
  const { eventId } = useParams();
  return useMutation({
    mutationKey: ['events', eventId, 'registration', 'form', 'update'],
    mutationFn: ({ payload, config }) => updateFormItems(eventId, payload, config),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: registrationFormOptions(eventId).queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: unlinkedFieldsOptions(eventId).queryKey,
      });
    },
  });
};

export const useRegistrationFormSection = (id, onError) => {
  const { eventId } = useParams();
  return useQuery({
    queryKey: ['events', eventId, 'registration', 'form', 'section', id],
    queryFn: () => getSection(eventId, id),
    onError,
    select: ({ data }) => data,
    enabled: !!id,
  });
};

export const useRegistrationCreateFormSectionMutation = () => {
  const queryClient = useQueryClient();
  const { eventId } = useParams();
  return useMutation({
    mutationKey: ['events', eventId, 'registration', 'form', 'section', 'create'],
    mutationFn: ({ payload }) => createSection(eventId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: registrationFormOptions(eventId).queryKey,
      });
    },
  });
};

export const updateFormSectionMutationOptions = (queryClient, eventId) => ({
  mutationKey: ['events', eventId, 'registration', 'form', 'section', 'update'],
  mutationFn: ({ sectionId, payload }) => updateSection(eventId, sectionId, payload),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: registrationFormOptions(eventId).queryKey,
    });

    queryClient.invalidateQueries({
      queryKey: unlinkedFieldsOptions(eventId).queryKey,
    });
  },
});

export const useRegistrationUpdateFormSectionMutation = () => {
  const queryClient = useQueryClient();
  const { eventId } = useParams();
  return useMutation(updateFormSectionMutationOptions(queryClient, eventId));
};

const getEmailTemplatesBaseQueryKey = (eventId, templateType) => [
  'event',
  eventId,
  'registration',
  'emailTemplates',
  'templateType',
  templateType,
];

export const emailTemplatesOptions = (eventId, templateType) => ({
  queryKey: getEmailTemplatesBaseQueryKey(eventId, templateType),
  queryFn: () => getEmailTemplates(eventId, templateType),
});

export const useEmailTemplates = templateType => {
  const { eventId } = useParams();
  return useQuery({
    ...emailTemplatesOptions(eventId, templateType),
    select: data => data[0] || null, // Select the first template or null
  });
};

export const useUpsertEmailTemplateMutation = templateType => {
  const { eventId } = useParams();
  const queryClient = useQueryClient();

  return useMutation(
    ({ templateId, ...payload }) => {
      if (templateId === NEW_EMAIL_TEMPLATE_ID) {
        return createEmailTemplate(eventId, payload);
      }

      return updateEmailTemplate(eventId, templateId, payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getEmailTemplatesBaseQueryKey(eventId, templateType));
      },
    },
  );
};
